import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { usePermission } from '@/permissions/usePermission';
import { buildMenu } from '@/app/navigation';
import type { MenuItem } from '@/app/navigation';

// First navigable (leaf-with-`to`) item in a permission-filtered menu tree.
function firstTo(items: MenuItem[]): string | undefined {
  for (const i of items) {
    if (i.to) return i.to;
    if (i.children) {
      const nested = firstTo(i.children);
      if (nested) return nested;
    }
  }
  return undefined;
}

// Role-aware default landing: redirect to the first page the current user can actually
// access (host → Clients, tenant-admin → Email Recipients, tenant-user → their first
// permitted feature). Replaces the hardcoded /admin/email-recipients redirect, which
// left host (and permission-limited) users on an "Access denied" page after login.
export function DefaultLanding() {
  const { user } = useAuth();
  const can = usePermission();
  const isHost = !!user?.isAuthenticated && !user.tenantId;
  const isAdmin = (user?.roles ?? []).some((r) => r.toLowerCase() === 'admin');

  const sections = buildMenu(isHost, isAdmin, can);
  for (const section of sections) {
    const to = firstTo(section.items);
    if (to) return <Navigate to={to} replace />;
  }
  // Nothing permitted in the menu — fall back to the always-available profile page.
  return <Navigate to="/profile" replace />;
}
